/**
 * Midgard's legal documents — DRAFTS for owner and legal review.
 *
 * Written from an audit of what Midgard actually does (October 2026).
 * Blocks of type `review` mark facts only the owner or a lawyer can supply;
 * they are shown on the page while the documents are drafts.
 * Versions live in legalVersions.json (shared with the backend).
 *
 * Block types: string (paragraph) | { list: [...] } | { review: '...' }
 */

// Owner clarification (2026-10-19): AFTERRA is a development brand, not a
// registered legal entity. The owner has a Swedish enskild firma (sole
// proprietorship with F-skatt) whose current status is unverified; it has no
// legal personality separate from the owner. A US formation is postponed.
const OPERATOR_REVIEW =
  'Responsible operator not yet confirmed. AFTERRA is a brand and cannot be named as the operator. ' +
  'Candidates: the owner personally, or the owner\'s Swedish enskild firma (which is the owner, trading under a registered name) once its registration and F-skatt status are verified. ' +
  'Needed: the operator\'s legal name, postal address, organisation or personal identity number as legally required, VAT number if any, and a contact email. ' +
  'Under the Swedish E-Commerce Act (lag om elektronisk handel, 8 §) and the EU e-Commerce Directive these details must be easy to find.';

export const LEGAL_DOCS = {
  terms: {
    summary:
      'The agreement between you and Midgard: who may join, what you may do here, what you keep and what you allow us to do with what you share.',
    sections: [
      {
        h: 'Who runs Midgard',
        body: [
          'Midgard is a Norse-inspired social platform created and developed under the AFTERRA brand. AFTERRA is a brand name, not a separate company. "Midgard", "we" and "us" in these Terms mean the operator named below, who is responsible for Midgard.',
          { review: OPERATOR_REVIEW },
        ],
      },
      {
        h: 'Accepting these Terms',
        body: [
          'By creating an account you agree to these Terms and to the Community Guidelines. If Midgard makes a material change, you will be asked to accept the new version before continuing to use your account. You can read every current document at any time from the About page.',
          'The Privacy Policy explains how personal data is handled. It is information, not something you are asked to agree to, and agreeing to these Terms is not consent to any optional processing.',
        ],
      },
      {
        h: 'Who can join',
        body: [
          'You need to be old enough to enter this agreement where you live, and at least the minimum age below. One person may hold one personal account. You must give accurate information when you register and keep your sign-in details to yourself.',
          { review: 'Minimum age to be decided. The age at which a child can consent to online services differs by country (GDPR art. 8: 13 in Sweden, 14 in Spain, up to 16 elsewhere); purchases with real money may need a higher age or parental involvement.' },
        ],
      },
      {
        h: 'Your account',
        body: [
          'You can sign in with an email and password or with Google. You are responsible for activity on your account; tell us promptly if you think someone else has accessed it.',
          'You can stop using Midgard at any time. To close your account and have your data deleted, contact us using the details in the Privacy Policy.',
          { review: 'Self-service account deletion and data export are not yet built; requests are currently handled manually.' },
        ],
      },
      {
        h: 'What you share',
        body: [
          'You keep ownership of the posts, Sagas, images, comments, messages and other content you create. To run Midgard you give us a worldwide, non-exclusive, royalty-free licence to host, store, reproduce, adapt for display (for example resizing images or creating video posters), publish and distribute that content within Midgard and in link previews of public pages, for as long as it remains on Midgard and for a short period afterwards in backups.',
          'You confirm you have the rights to what you upload, and that it does not infringe anyone else\'s rights or break the law.',
          'Public posts and profiles can be seen by other members and, in link previews, by anyone a link is shared with. Private messages are visible to you and the person you write to; they are stored on our servers and are not end-to-end encrypted.',
        ],
      },
      {
        h: 'Conduct',
        body: [
          'Follow the Community Guidelines. In particular, do not harass people, post illegal content, impersonate others, spam, try to access accounts or data that are not yours, interfere with the service, or use automated means to scrape it.',
        ],
      },
      {
        h: 'Moderation, notices and appeals',
        body: [
          'Anyone can report content or accounts with the report tools in Midgard. Reports are reviewed by Midgard\'s moderators (the Wardens). Automated rules may flag text for review; final decisions about removals and account restrictions are made by people.',
          'If we remove or restrict your content or account, you can ask for the decision to be reviewed through the contact details below, and a different moderator will look at it where possible.',
          { review: 'Members are not yet notified automatically when moderators hide or remove their content. A notice with the reasons (DSA art. 17 statement of reasons) needs to be built before these Terms are final.' },
          { review: 'Check which Digital Services Act duties apply (notice-and-action, statements of reasons, internal complaints, point of contact); some are reduced for micro and small enterprises. A single point of contact for authorities and members must be published.' },
        ],
      },
      {
        h: 'Suspension and termination',
        body: [
          'We may restrict, suspend or close an account that seriously or repeatedly breaks these Terms or the Community Guidelines, or where the law requires it. Where it is proportionate we will warn first. You can stop using Midgard and ask for account closure at any time.',
        ],
      },
      {
        h: 'Runegold, purchases and creator support',
        body: [
          'Runegold is Midgard\'s in-world currency. It has no cash value, cannot be exchanged for money and is governed by the Runegold & Rewards rules, which form part of these Terms.',
          'Some features let you pay with real money through Stripe: buying Runegold, Premium, advertising, and tipping or subscribing to creators. Prices are shown before you pay. Stripe processes your payment details; Midgard does not receive or store your card number.',
          { review: 'Add consumer information required for paid digital content: right of withdrawal and its waiver for immediate supply, refunds, taxes, and the seller of record for creator tips and subscriptions.' },
        ],
      },
      {
        h: 'oDIN',
        body: [
          'oDIN is an AI guide. Its answers can be wrong; do not rely on them for medical, legal, financial or safety decisions. How oDIN works and which services process your conversations is explained in the oDIN & AI Notice.',
        ],
      },
      {
        h: 'Referral programme',
        body: ['A referral programme is planned but not active. No referral rewards are offered or owed until separate terms for it are published.'],
      },
      {
        h: 'Availability and changes',
        body: [
          'Midgard is in active development. Features may change, be paused or be removed, and parts of the world are clearly marked as not yet open. We work to keep the service available and your data safe, but cannot promise uninterrupted service.',
        ],
      },
      {
        h: 'Liability',
        body: [
          'Nothing in these Terms limits liability that cannot be limited by law, including your rights as a consumer.',
          { review: 'Choosing a US formation later would not remove Swedish, Spanish or EU obligations towards EU members. Liability limitations, applicable law and competent courts to be drafted by a lawyer. For consumers in the EU, mandatory consumer protections of their country of residence apply regardless of the chosen law.' },
        ],
      },
      {
        h: 'Contact',
        body: [{ review: 'Contact email and postal address for the operator.' }],
      },
    ],
  },

  privacy: {
    summary:
      'What personal data Midgard handles, why, who helps us process it, how long it is kept and the rights you have.',
    sections: [
      {
        h: 'Controller',
        body: [{ review: OPERATOR_REVIEW + ' State whether a Data Protection Officer is appointed (likely not required at this scale; to be confirmed).' }],
      },
      {
        h: 'What we process',
        body: [{
          list: [
            'Account: email address, username, display name, a securely hashed password (never the password itself), sign-in method, account role and creation date. If you sign in with Google: your Google account identifier, email, name and profile photo.',
            'Profile: bio, profile photo, banner and other details you add.',
            'Content: posts, Sagas (short videos), stories (shown for 24 hours), comments, images and videos you upload, likes, reposts, bookmarks and follows.',
            'Private messages between you and other members.',
            'Notifications, and push-notification subscriptions if you turn push on.',
            'Runegold and payments: balance, transfers, donations, rewards and purchase records. Card payments are handled by Stripe; we receive the outcome and a reference, not your card details.',
            'World activity: your position and presence in shared world areas, inventory, properties and items.',
            'oDIN conversations, saved in your Records.',
            'Moderation: reports you make or that concern you, and moderation decisions.',
            'Technical data: IP address and request details in server logs, sign-in rate-limit counters, and the sign-in token stored in your browser.',
          ],
        }],
      },
      {
        h: 'Why, and on what legal basis',
        body: [{
          list: [
            'To provide Midgard and your account, publish what you share, deliver messages and run Runegold and the world: performance of our contract with you (GDPR art. 6(1)(b)).',
            'To keep Midgard safe: preventing abuse, brute-force sign-ins and fraud, moderating content, and securing systems: our legitimate interest in a safe service (art. 6(1)(f)).',
            'To answer oDIN questions using external AI and knowledge services: performance of the contract when you choose to use oDIN.',
            'Push notifications: your consent, given in your browser, which you can withdraw at any time.',
            'Keeping payment and tax records: legal obligation (art. 6(1)(c)).',
          ],
        }, 'Midgard does not sell personal data, does not use it for third-party advertising and, as of this version, does not use analytics or tracking cookies.'],
      },
      {
        h: 'Who processes data for us',
        body: [{
          list: [
            'OVHcloud: hosting of Midgard\'s servers and database (data centre in Canada).',
            'Groq: runs the AI models behind oDIN. Receives your oDIN messages, recent conversation context and the Midgard information needed for the answer.',
            'Wikimedia (Wikidata and Wikipedia) and MET Norway: receive a place name, search term or coordinates when oDIN looks up places, reference information or weather.',
            'Google: sign-in with Google (if you use it), web fonts loaded by your browser, and profile photos of Google accounts.',
            'DiceBear: generates default avatar images, loaded by your browser.',
            'Stripe: payment processing for purchases, tips and subscriptions.',
            'Your browser\'s push service (for example Google, Mozilla or Apple): delivers push notifications if you enable them.',
          ],
        }, { review: 'Confirm data processing agreements with each provider and their roles (processor or independent controller).' }],
      },
      {
        h: 'International transfers',
        body: [
          'Midgard is hosted in Canada, which the European Commission recognises as providing adequate protection for commercial organisations. Groq, Google, Stripe and Wikimedia are based in the United States.',
          { review: 'Confirm the transfer mechanism for each US provider (EU-US Data Privacy Framework certification or Standard Contractual Clauses).' },
        ],
      },
      {
        h: 'How long we keep data',
        body: [
          'Stories disappear after 24 hours. Account data and content are kept while your account exists; when content or an account is deleted it is removed from the live service, and from backups when they expire. Server backups are kept for 14 days.',
          { review: 'Set retention periods for server logs, moderation records, payment records (tax law), oDIN conversations and closed accounts.' },
        ],
      },
      {
        h: 'Your rights',
        body: [
          'You can ask for access to your data, correction, deletion, restriction, a portable copy, and you can object to processing based on legitimate interests. Where processing is based on consent you can withdraw it at any time. Contact us using the details above; we answer within one month.',
          'You can also complain to the data protection authority where you live, work or think your rights were infringed, for example the Swedish Authority for Privacy Protection (IMY, www.imy.se) or the Spanish Data Protection Agency (AEPD, www.aepd.es).',
          { review: 'The lead supervisory authority follows the operator\'s place of establishment (Sweden if the enskild firma or the owner is established there). Confirm once the operator is settled.' },
        ],
      },
      {
        h: 'Security',
        body: [
          'Connections are encrypted (HTTPS), passwords are hashed, access to private data requires your sign-in, and sign-in attempts are rate-limited. No system is perfectly secure; if a breach affects your data we will inform you and the authorities as the law requires.',
        ],
      },
      {
        h: 'Children',
        body: ['Midgard is not intended for children under the minimum age in the Terms of Service.'],
      },
    ],
  },

  cookies: {
    summary:
      'Which cookies and browser storage Midgard uses. Today everything is necessary for the site to work; nothing tracks you for analytics or advertising.',
    sections: [
      {
        h: 'Our approach',
        body: [
          'Midgard only uses storage that is strictly necessary to provide the service you ask for, or that remembers a choice you made. These do not need your consent under the EU ePrivacy rules as applied in Sweden (lag om elektronisk kommunikation, ch. 9 § 28) and Spain (LSSI art. 22.2), but you should know about them. If optional cookies (for example analytics) are ever added, they will stay off until you switch them on, and you will be able to change your mind at any time on this page.',
        ],
      },
      {
        h: 'What is stored',
        body: [{
          list: [
            'midgard_media (cookie, 7 days, secure, not readable by scripts): lets your browser load images and videos from Midgard\'s media server while you are signed in.',
            'midgard_token (browser storage): keeps you signed in. Removed when you log out.',
            'midgard_theme, midgard_settings, midgard_graphics_profile, midgard_show_perf_stats (browser storage): your appearance and world-display settings.',
            'midgard_odin_notice_v3 (browser storage): remembers that you have read oDIN\'s processing notice.',
            'midgard_push_declined (browser storage): remembers that you declined push notifications.',
            'midgard_privacy_choices (browser storage): your choices on this page.',
            'Push notification registration (browser service worker): only if you enable push notifications.',
          ],
        }],
      },
      {
        h: 'Third parties your browser contacts',
        body: [
          'Google Fonts (font files), DiceBear (default avatars), Google (profile photos of Google accounts) and Stripe (payment pages) receive your IP address when your browser loads their content. They do not set tracking cookies through Midgard pages.',
          { review: 'Consider self-hosting fonts and default avatars so no third party is contacted on ordinary page views.' },
        ],
      },
    ],
  },

  guidelines: {
    summary: 'How we treat each other in Midgard.',
    sections: [
      {
        h: 'The spirit of the hall',
        body: ['Midgard is a shared world. Bring your stories, your craft and your curiosity, and leave room for everyone else to do the same. Disagreement is welcome; contempt is not.'],
      },
      {
        h: 'Not allowed',
        body: [{
          list: [
            'Harassment, threats, bullying or targeting someone with abuse.',
            'Hate against people for who they are, including ethnicity, religion, disability, gender, sexual orientation or nationality.',
            'Sexual content involving minors, in any form. This is reported to the authorities.',
            'Sexually explicit content, graphic violence or gore shared without warning or in public spaces.',
            'Illegal content and the promotion of illegal activity.',
            'Impersonating other people, members, staff or the mythic characters run by Midgard.',
            'Sharing someone\'s private information without permission.',
            'Spam, scams, manipulation of Runegold, rewards or engagement, and multiple accounts used to deceive.',
            'Attempting to break into accounts, exploit bugs for gain, or disrupt the service.',
          ],
        }],
      },
      {
        h: 'Found a bug?',
        body: ['Report it through the bug-report channel on our Discord instead of exploiting it. Verified reports may be rewarded with Runegold (see Runegold & Rewards).'],
      },
      {
        h: 'Reporting and what happens next',
        body: ['Use the report option on any post, comment, Saga or profile. The Wardens review reports and may remove content, warn, restrict or suspend accounts. You can ask for any decision about your content or account to be reviewed.'],
      },
    ],
  },

  runegold: {
    summary: 'What Runegold is, how you earn and spend it, and the rules for community rewards.',
    sections: [
      {
        h: 'What Runegold is',
        body: [
          'Runegold is a virtual currency that exists only inside Midgard. It has no monetary value, cannot be exchanged for real money or other assets, and is not a payment instrument or investment. Your Runegold balance is a licence to use in-world features, not property.',
          'Future features that would let Runegold be redeemed or used financially are not available and would only arrive after implementation, eligibility checks and legal review.',
        ],
      },
      {
        h: 'Getting and using Runegold',
        body: [{
          list: [
            'New members receive a welcome balance.',
            'Runegold can be bought with real money through Stripe. Purchased Runegold is not refundable once delivered, except where the law requires otherwise.',
            'You can send Runegold to other members, donate it to creators, and spend it in the world.',
            'Transfers are final once confirmed. Midgard may reverse transfers that result from errors, bugs, fraud or a breach of these rules.',
          ],
        }, { review: 'Confirm refund and withdrawal-right wording for purchased Runegold with a lawyer.' }],
      },
      {
        h: 'Bug-report rewards',
        body: [{
          list: [
            'Report bugs through the bug-report process on Midgard\'s Discord.',
            'An administrator reviews each report by hand.',
            'Verified, useful reports may receive 1 to 5 Runegold.',
            'Duplicates, spam, invented or exaggerated reports do not qualify.',
            'Rewards are discretionary and never guaranteed, and are not paid automatically.',
            'Larger grants for moderators or exceptional contributions, if offered, are handled separately by an administrator.',
          ],
        }],
      },
      {
        h: 'Referral programme',
        body: ['Coming soon. Nothing is offered or earned through referrals until its rules are published.'],
      },
    ],
  },

  ai: {
    summary: 'oDIN is an AI system. This notice explains what that means for you.',
    sections: [
      {
        h: 'You are talking to an AI',
        body: ['oDIN is an artificial-intelligence guide, not a person. His answers are generated by language models together with Midgard\'s own knowledge services, and they can be inaccurate or incomplete.'],
      },
      {
        h: 'What oDIN uses',
        body: [{
          list: [
            'Your messages and recent conversation, sent to Groq to generate answers.',
            'Midgard\'s own information needed for the answer, such as world lore, public profiles or, when you ask about it, a short summary of your own items or account.',
            'For weather, places and reference questions, only the place name or a short search term goes to Wikidata, Wikipedia and MET Norway.',
            'The time, your Runegold balance and simple Midgard facts are answered inside Midgard without any AI provider.',
          ],
        }],
      },
      {
        h: 'Storage and your choices',
        body: [
          'Conversations are saved in your Records so you can return to them, and you can delete them there. oDIN is optional: Midgard works fully without it.',
          'Closing oDIN\'s information notice only records that you have seen it. It is not consent to anything else.',
        ],
      },
      {
        h: 'Limits',
        body: ['oDIN does not make decisions about your account, moderation or Runegold. Do not share sensitive personal information with oDIN.'],
      },
    ],
  },
};
